import { inject, Injectable } from '@angular/core';
import { FormGroup } from '@angular/forms';

import {
  AffectedQuestionCard,
  AffectedQuestionOption,
  CarouselType,
  ParsedConditionalAnswerValue,
  SurveyConditionalEvaluationEnvironment,
  evaluateConditionSet,
  getConditionTriggerIds,
  mapItemsById,
  Question as BaseQuestion,
  QuestionCard,
  QuestionOption,
  SurveyResult
} from '@empathy/survey/base';

import { SurveyAnswerParserService } from './survey-answer-parser.service';
import { QuestionControl } from '../models/question-control.interface';

@Injectable({ providedIn: 'root' })
export class SurveyConditionalService {
  answers: SurveyResult;
  questions: Record<string, BaseQuestion> = {};

  private questionControls: QuestionControl = {};
  private mappedTriggerAndAffectedQuestions: Record<string, string[]> = {};
  private mappedTriggerQuestionAndAffectedCards: Record<string, AffectedQuestionCard[]> = {};
  private mappedTriggerQuestionAndAffectedOptions: Record<string, AffectedQuestionOption[]> = {};
  private parsedAnswers: Record<string, ParsedConditionalAnswerValue> = {};
  private questionOptionLookup: Record<string, Record<string, QuestionOption>> = {};
  private questionCardLookup: Record<string, Record<string, QuestionCard>> = {};
  private skipControlsReset = false;
  private surveyAnswerParserService = inject(SurveyAnswerParserService);

  mapConditionality(questionId: string, mappedQuestions: Record<string, BaseQuestion>, questionControls: QuestionControl, answers: SurveyResult): void {
    const mappedQuestion = mappedQuestions[questionId];

    if (!mappedQuestion) {
      return;
    }

    this.questions = mappedQuestions;
    this.questionControls = questionControls;
    this.answers = answers;
    this.indexQuestionChildren(mappedQuestion);

    this.mapQuestionConditionality(mappedQuestion);
    if (mappedQuestion.options?.length > 0) {
      mappedQuestion.options.forEach((option) => this.mapQuestionOptionConditionality(mappedQuestion.id, option));
    }
    if (mappedQuestion.cards?.length > 0) {
      mappedQuestion.cards.forEach((card) => this.mapQuestionCardConditionality(mappedQuestion.id, card));
    }
  }

  runStaticSurveyConditionality(questions: BaseQuestion[], answers: SurveyResult): void {
    this.questions = {};
    this.answers = answers;
    this.parsedAnswers = {};
    this.questionOptionLookup = {};
    this.questionCardLookup = {};

    questions.forEach((question) => {
      this.questions[question.id] = question;
      this.indexQuestionChildren(question);
    });

    questions.forEach((question) => {
      if (question.visibleWhen?.length > 0) {
        question.visible = evaluateConditionSet(question.visibleWhen, this.getEvaluationEnvironment());
      }
    });
  }

  run(questionId: string): void {
    delete this.parsedAnswers[questionId];
    this.runConditionality(this.mappedTriggerAndAffectedQuestions[questionId]);
    this.runOptionConditionality(this.mappedTriggerQuestionAndAffectedOptions[questionId]);
    this.runCardConditionality(this.mappedTriggerQuestionAndAffectedCards[questionId]);
  }

  runAll(questionIds: string[], options?: { skipReset?: boolean }): void {
    const previousSkipControlsReset = this.skipControlsReset;
    this.skipControlsReset = options?.skipReset ?? previousSkipControlsReset;

    questionIds.forEach((questionId) => this.run(questionId));

    this.skipControlsReset = previousSkipControlsReset;
  }

  destroy(): void {
    this.questions = {};
    this.questionControls = {};
    this.mappedTriggerAndAffectedQuestions = {};
    this.mappedTriggerQuestionAndAffectedOptions = {};
    this.mappedTriggerQuestionAndAffectedCards = {};
    this.parsedAnswers = {};
    this.questionOptionLookup = {};
    this.questionCardLookup = {};
    this.skipControlsReset = false;
  }

  private runConditionality(affectedQuestionIds: string[]): void {
    if (!affectedQuestionIds || affectedQuestionIds.length === 0) {
      return;
    }

    affectedQuestionIds.forEach((qId) => {
      const question = this.questions[qId];
      const wasVisible = question.visible === true;
      question.visible = evaluateConditionSet(question.visibleWhen, this.getEvaluationEnvironment());

      const qControl = this.questionControls[qId];

      if (!question.visible) {
        qControl.disable();
        if (!this.skipControlsReset && wasVisible) {
          qControl.reset(undefined);
        }
      } else {
        qControl.enable();
        if (CarouselType.includes(question.type)) {
          this.applyCarouselCardControlVisibility(qId);
        }
      }

      this.refreshQuestionReference(qId);
    });
  }

  private runOptionConditionality(affectedQuestionOptionIds: AffectedQuestionOption[]): void {
    if (!affectedQuestionOptionIds || affectedQuestionOptionIds.length === 0) {
      return;
    }

    const affectedQuestionIds = new Set<string>();
    const resetQuestionIds = new Set<string>();

    affectedQuestionOptionIds.forEach(({ questionId, optionId }) => {
      affectedQuestionIds.add(questionId);
      const question = this.questions[questionId];
      const option = this.questionOptionLookup[questionId]?.[optionId] ?? question.options.find((opt) => opt.id === optionId);
      if (option) {
        const wasVisible = option.visible === true;
        option.visible = evaluateConditionSet(option.visibleWhen, this.getEvaluationEnvironment());
        if (wasVisible !== option.visible) {
          resetQuestionIds.add(questionId);
        }
      }
    });

    if (!this.skipControlsReset) {
      resetQuestionIds.forEach((questionId) => this.questionControls[questionId].reset(undefined));
    }

    affectedQuestionIds.forEach((questionId) => this.refreshQuestionReference(questionId));
  }

  private runCardConditionality(affectedQuestionCardIds: AffectedQuestionCard[]): void {
    if (!affectedQuestionCardIds || affectedQuestionCardIds.length === 0) {
      return;
    }

    const affectedQuestionIds = new Set<string>();
    const resetQuestionIds = new Set<string>();

    affectedQuestionCardIds.forEach(({ questionId, cardId }) => {
      affectedQuestionIds.add(questionId);
      const question = this.questions[questionId];
      const card = this.questionCardLookup[questionId]?.[cardId] ?? question.cards.find((c) => c.id === cardId);
      if (!card) {
        return;
      }

      const wasVisible = card.visible === true;
      card.visible = evaluateConditionSet(card.visibleWhen, this.getEvaluationEnvironment());
      if (wasVisible !== card.visible) {
        resetQuestionIds.add(questionId);
      }

      if (!card.visible) {
        (this.questionControls[questionId] as FormGroup).controls[card.id].disable();
      } else {
        (this.questionControls[questionId] as FormGroup).controls[card.id].enable();
      }
    });

    if (!this.skipControlsReset) {
      resetQuestionIds.forEach((questionId) => this.questionControls[questionId].reset(undefined));
    }

    affectedQuestionIds.forEach((questionId) => this.refreshQuestionReference(questionId));
  }

  private applyCarouselCardControlVisibility(questionId: string): void {
    const question = this.questions[questionId];
    const questionControl = this.questionControls[questionId] as FormGroup;

    question.cards?.forEach((card) => {
      const cardControl = questionControl.controls[card.id];
      if (!cardControl) {
        return;
      }

      if (card.visible) {
        cardControl.enable();
      } else {
        cardControl.disable();
      }
    });
  }

  private mapQuestionConditionality(question: BaseQuestion): void {
    getConditionTriggerIds(question.visibleWhen).forEach((triggerQuestionId) => {
      this.mappedTriggerAndAffectedQuestions[triggerQuestionId] = this.mappedTriggerAndAffectedQuestions[triggerQuestionId] || [];
      this.mappedTriggerAndAffectedQuestions[triggerQuestionId].push(question.id);
    });
  }

  private mapQuestionOptionConditionality(questionId: string, option: QuestionOption): void {
    getConditionTriggerIds(option.visibleWhen).forEach((triggerQuestionId) => {
      this.mappedTriggerQuestionAndAffectedOptions[triggerQuestionId] = this.mappedTriggerQuestionAndAffectedOptions[triggerQuestionId] || [];
      this.mappedTriggerQuestionAndAffectedOptions[triggerQuestionId].push({ questionId, optionId: option.id });
    });
  }

  private mapQuestionCardConditionality(questionId: string, card: QuestionCard): void {
    getConditionTriggerIds(card.visibleWhen).forEach((triggerQuestionId) => {
      this.mappedTriggerQuestionAndAffectedCards[triggerQuestionId] = this.mappedTriggerQuestionAndAffectedCards[triggerQuestionId] || [];
      this.mappedTriggerQuestionAndAffectedCards[triggerQuestionId].push({ questionId, cardId: card.id });
    });
  }

  private indexQuestionChildren(question: BaseQuestion): void {
    this.questionOptionLookup[question.id] = this.mapItemsById(question.options);
    this.questionCardLookup[question.id] = this.mapItemsById(question.cards);
  }

  private refreshQuestionReference(questionId: string): void {
    const question = this.questions[questionId];
    if (!question) {
      return;
    }

    this.questions[questionId] = this.cloneWithPrototype(question);
  }

  private mapItemsById<T extends QuestionOption | QuestionCard>(items: T[] = []): Record<string, T> {
    return mapItemsById(items);
  }

  private getEvaluationEnvironment(): SurveyConditionalEvaluationEnvironment {
    return {
      answers: this.answers,
      questions: this.questions,
      parsedAnswers: this.parsedAnswers,
      parseAnswerValue: (value: unknown) => this.surveyAnswerParserService.parseAnswerValue(value)
    };
  }

  private cloneWithPrototype<T extends object>(item: T): T {
    return Object.assign(Object.create(Object.getPrototypeOf(item)), item);
  }
}
